import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Pencil, Zap, Calendar, Package, X, FileText, Copy } from "lucide-react";

const tomorrow = () => new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);

export default function Templates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "dispatcher";
  const basePath = isAdmin ? "/admin/orders" : "/portal/orders";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [useOpen, setUseOpen] = useState(null);
  const [date, setDate] = useState(tomorrow());
  const [extraNote, setExtraNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = isAdmin && filterCustomer ? { customer_id: filterCustomer } : {};
      const { data } = await api.get("/order-templates", { params });
      setRows(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (isAdmin) api.get("/customers").then(r => setCustomers(r.data || []));
  }, [isAdmin]);
  useEffect(() => { load(); }, [filterCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = async (tpl) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/order-templates/${tpl.id}/use`, {
        delivery_date: date,
        notes: extraNote || null,
      });
      setBusy(false);
      setUseOpen(null);
      navigate(`${basePath}/${data.id}`);
    } catch (e) { setError(formatApiError(e)); setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Vorlage wirklich löschen?")) return;
    await api.delete(`/order-templates/${id}`);
    load();
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--gold)" }}>Vorlagen</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Auftrags-Vorlagen</h1>
            <p className="text-sm text-[var(--text-2)] mt-1">
              Wiederkehrende Aufträge als Vorlage speichern — neuer Auftrag mit einem Klick.
            </p>
          </div>
          {isAdmin && (
            <select
              data-testid="templates-filter-customer"
              className="input-base max-w-xs"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
            >
              <option value="">Alle Kunden</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </header>

        {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Name</th>
                {isAdmin && <th className="text-left py-3 px-4">Kunde</th>}
                <th className="text-left py-3 px-4">Abhol</th>
                <th className="text-left py-3 px-4">Lieferung</th>
                <th className="text-right py-3 px-4">Paletten</th>
                <th className="text-right py-3 px-4">Verwendet</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody data-testid="templates-table">
              {loading && <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-10 text-[var(--text-3)]">Lade…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-10 text-[var(--text-3)]">
                  Noch keine Vorlagen. Öffne einen Auftrag und klicke <strong>„Als Vorlage speichern“</strong>.
                </td></tr>
              )}
              {!loading && rows.map(t => {
                const pallets = (t.positions || []).reduce((s, p) => s + (Number(p.quantity) || 0), 0);
                return (
                  <tr key={t.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]" data-testid={`template-row-${t.id}`}>
                    <td className="py-3 px-4 text-[var(--text)] font-medium">{t.name}</td>
                    {isAdmin && <td className="py-3 px-4 text-[var(--text-2)]">{t.customer_name || "—"}</td>}
                    <td className="py-3 px-4 text-[var(--text-2)] text-xs">
                      {(t.pickup?.zip || "")} {(t.pickup?.city || "")}
                    </td>
                    <td className="py-3 px-4 text-[var(--text-2)] text-xs">
                      {(t.delivery?.zip || "")} {(t.delivery?.city || "")}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-[var(--text-2)]">{pallets || "—"}</td>
                    <td className="py-3 px-4 text-right text-[var(--text-3)] text-xs">{t.use_count || 0}×</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setDate(tomorrow()); setExtraNote(""); setUseOpen(t); }}
                        data-testid={`template-use-${t.id}`}
                        className="btn-gold px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider inline-flex items-center gap-1 mr-2"
                      >
                        <Zap size={12} /> 1-Klick
                      </button>
                      <button
                        onClick={() => del(t.id)}
                        data-testid={`template-delete-${t.id}`}
                        className="text-[var(--text-3)] hover:text-red-700"
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {useOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setUseOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-md p-6 fade-in" data-testid="template-use-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Auftrag aus Vorlage</h2>
              <button onClick={() => setUseOpen(null)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            <div className="mb-4 pb-4 border-b border-[var(--border)]">
              <div className="label-eyebrow mb-1">Vorlage</div>
              <div className="text-sm font-medium" data-testid="template-use-name">{useOpen.name}</div>
              <div className="text-xs text-[var(--text-2)] mt-1 flex flex-wrap gap-x-4">
                <span><Package size={11} className="inline mr-1" />{(useOpen.positions || []).reduce((s, p) => s + (Number(p.quantity) || 0), 0)} Pal.</span>
                <span>{useOpen.pickup?.city || "—"} → {useOpen.delivery?.city || "—"}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow block mb-2 flex items-center gap-2"><Calendar size={11} /> Lieferdatum *</label>
                <input
                  type="date"
                  required
                  data-testid="template-use-date"
                  className="input-base"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2 flex items-center gap-2"><FileText size={11} /> Zusatz-Bemerkung (optional)</label>
                <textarea
                  data-testid="template-use-note"
                  rows={2}
                  className="input-base"
                  value={extraNote}
                  onChange={(e) => setExtraNote(e.target.value)}
                  placeholder="Wird zur Vorlage-Notiz hinzugefügt"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setUseOpen(null)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
              <button
                onClick={() => applyTemplate(useOpen)}
                disabled={busy || !date}
                data-testid="template-use-submit"
                className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider flex items-center gap-2"
              >
                <Zap size={14} /> {busy ? "Erstelle…" : "Auftrag erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
